import { useCallback, useState } from 'react';
import { useMeterModels } from 'api/hooks/meter-models/useMeterModels';
import { Table } from 'components';
import { useParams } from 'react-router-dom';
import { RoleName, type SerializedMeterModel } from '@enmon/commons/lib/tools';
import { useDeleteMeterModel } from 'api/hooks/meter-models/useDeleteMeterModel';
import type { TablePaginationConfig } from 'antd';
import type { Key, SorterResult, SortOrder } from 'antd/lib/table/interface';
import { getMeterModelsColumnsConfig } from '../utils/getColumnsConfig';
import { useMeterModelsFilterWithPagination } from '../hooks/useMeterModelsFilterWithPagination';
import { useUserInfo } from '../../../../../../../../../hooks/useUser';

export const MeterModelsTable = () => {
  const { id: manufacturerId } = useParams<{ id: string }>();
  const { role } = useUserInfo() ?? {};
  const isMagicRole = role?.name === RoleName.Magic;

  // State
  const { filter, pagination, setPaginationState, setFilterAndPaginationState } = useMeterModelsFilterWithPagination();
  const [sortState, setSortState] = useState<{ field: Key | null; order: SortOrder }>({
    field: null,
    order: null,
  });

  // Fetch
  const { data: modelsResponse, isFetching: isFetchingModels } = useMeterModels(
    {
      ...(filter?.search && { search: filter?.search }),
      ...(filter?.commodity && { commodity: filter?.commodity }),
      manufacturerId: Number(manufacturerId),
      populate: ['manufacturer'],
      sort: filter?.sort,
      page: pagination.current,
      pageSize: pagination.pageSize,
    },
    {
      onSuccess: data => {
        setPaginationState({ total: data?.meta?.pagination?.total });
      },
    },
  );
  const { mutate: deleteModel, isLoading: isDeletingModels } = useDeleteMeterModel();
  const modelsData = modelsResponse?.data ?? [];

  const handleDelete = useCallback(
    (id: SerializedMeterModel['id']) => {
      deleteModel(id);
    },
    [deleteModel],
  );

  const handleTableChange = (
    newPagination: TablePaginationConfig,
    _: any,
    sorter: SorterResult<SerializedMeterModel> | SorterResult<SerializedMeterModel>[],
  ) => {
    const { columnKey, order } = (Array.isArray(sorter) ? sorter[0] : sorter) ?? {};
    const newFilter = {
      ...(columnKey &&
        order && {
          sort: { [columnKey.toString()]: `${order.slice(0, -3)}` },
        }),
    };

    setSortState({ field: columnKey, order });
    setFilterAndPaginationState(newFilter, newPagination);
  };

  // Table Config
  const isLoading = isFetchingModels || isDeletingModels;

  const tableColumns = getMeterModelsColumnsConfig({
    loading: isLoading,
    isMagicRole,
    onDelete: handleDelete,
    sortState,
  });

  return (
    <Table<SerializedMeterModel>
      columns={tableColumns}
      dataSource={modelsData}
      loading={isLoading}
      pagination={{
        ...pagination,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} models`,
      }}
      rowKey={item => item.id}
      scroll={{ x: 'fit-content' }}
      onChange={handleTableChange}
    />
  );
};
